import {
  AnswerType,
  BRANCH_NODE_KINDS,
  ICondition,
  IEditorNode,
  IEditorSection,
  insertAt,
  isUnary,
  moveItem,
  newSection,
  NodeKind,
  NodePath,
  OPERATOR_LABEL,
  answerLabel,
  removeAt,
  replaceAt,
  walkNodes,
} from './editorModel';

/**
 * Das Modell des visuellen Baukastens (#1245).
 *
 * Der Baukasten ist eine zweite Ansicht auf **dasselbe** kanonische Modell wie der
 * Listen-Editor — Abschnitte mit geordneten Elementen, Bedingungen als einzelne Vergleiche.
 * Es gibt keine eigene Graphform, die gespeichert würde: jede Linie auf der Fläche IST eine
 * Bedingung im Modell, jede Karte IST ein Abschnitt. Damit laufen Listen-Editor, Prüfung,
 * Runtime und Word-Add-in nicht auseinander, und nichts geht beim Wechsel der Ansicht verloren.
 *
 * Positionen werden nicht gespeichert, sondern berechnet: das Backend verwirft Zusatzfelder an
 * Abschnitten, und Zusatzfelder an Elementen gingen in den Inhalts-Hash ein (eine verschobene
 * Karte wäre eine neue Version). Die Reihenfolge von oben nach unten ist die Reihenfolge der
 * Urkunde — wer eine Karte verschiebt, ändert die Reihenfolge, nicht eine Koordinate.
 *
 * Reine Funktionen, weil hier die Fachlichkeit steckt: welche Linie welche Bedingung ist, was
 * eine Verbindung bewirkt und welche Verbindung sich selbst blockieren würde.
 */

// ---------------------------------------------------------------------------
// Maße. Zeilen haben feste Höhen, damit Layout und Anschlüsse ohne Messen stimmen.
// ---------------------------------------------------------------------------

export const SECTION_WIDTH = 400;
export const HEADER_HEIGHT = 52;
export const ROW_HEIGHT = 30;
export const ANSWER_HEIGHT = 24;
export const EMPTY_HEIGHT = 34;
export const CARD_PADDING = 8;
export const SECTION_GAP = 56;
export const TERMINAL_HEIGHT = 40;
export const TERMINAL_WIDTH = 160;
export const INDENT = 18;
export const CHECK_WIDTH = 240;
export const CHECK_HEIGHT = 64;
export const CHECK_GAP = 14;
/** Abstand der ersten Spur vom Kartenrand, dann je Spur. */
export const LANE_BASE = 22;
export const LANE_STEP = 14;

// ---------------------------------------------------------------------------
// Bezüge
// ---------------------------------------------------------------------------

/** Wo eine Bedingung sitzt: am Abschnitt (`path === null`) oder an einem Element. */
export interface ITargetRef {
  sectionIndex: number;
  path: NodePath | null;
}

export type AnswerKind = 'yes' | 'no' | 'option' | 'any';
export type EdgeKind = AnswerKind | 'field';

export interface ICanvasAnswer {
  handleId: string;
  label: string;
  kind: AnswerKind;
  /** Die Bedingung, die eine Linie von hier aus setzt. */
  condition: ICondition;
  /** Mitte des Anschlusses, gemessen vom oberen Kartenrand. */
  y: number;
}

export interface ICanvasRow {
  key: string;
  path: NodePath;
  depth: number;
  node: IEditorNode;
  /** Oberkante in der Karte. */
  y: number;
  height: number;
  answers: ICanvasAnswer[];
  /** Ein bedingtes Element nimmt eine Linie an. */
  targetHandleId: string | null;
}

export interface ISectionLayout {
  id: string;
  index: number;
  section: IEditorSection;
  x: number;
  y: number;
  width: number;
  height: number;
  rows: ICanvasRow[];
  targetHandleId: string;
}

/** Eine Prüfung auf Vorgangsdaten (oder einen festen Wert) — eine Quelle für Linien. */
export interface IFieldCheckLayout {
  id: string;
  condition: ICondition;
  x: number;
  y: number;
  /** Noch mit keinem Ziel verbunden — lebt nur in der Ansicht. */
  pending: boolean;
}

/** Eine Frage aus einem eingefügten Unterbaustein: Quelle, aber nicht hier bearbeitbar. */
export interface IExternalQuestionLayout {
  id: string;
  questionId: string;
  prompt: string;
  answerType: AnswerType;
  options: string[];
  x: number;
  y: number;
  height: number;
  answers: ICanvasAnswer[];
}

export interface IConditionEdge {
  id: string;
  source: string;
  sourceHandle: string;
  target: string;
  targetHandle: string;
  kind: EdgeKind;
  label: string;
  /** Die Frage steht weiter unten als ihr Ziel — sie wird vorgezogen gestellt. */
  backward: boolean;
  lane: number;
  targetRef: ITargetRef;
  questionId: string | null;
}

export interface IAnswerSource {
  nodeId: string;
  handleId: string;
  questionId: string | null;
  condition: ICondition;
}

export interface IBaukastenGraph {
  sections: ISectionLayout[];
  checks: IFieldCheckLayout[];
  externals: IExternalQuestionLayout[];
  edges: IConditionEdge[];
  start: { x: number; y: number };
  end: { x: number; y: number };
  /** Quelle je `${nodeId}|${handleId}`. */
  sources: Map<string, IAnswerSource>;
  /** Ziel je `${nodeId}|${handleId}`. */
  targets: Map<string, ITargetRef>;
  /** Wo jede Frage dieses Baums steht. */
  questionLocation: Map<string, { sectionIndex: number; path: NodePath }>;
  /** x der rechten Spalte (Prüfungen, fremde Fragen). */
  sideColumnX: number;
}

export interface IExternalQuestion {
  questionId: string;
  prompt: string;
  answerType: string;
  options?: string[];
  blockName?: string;
}

export const sectionNodeId = (section: IEditorSection): string =>
  `s:${section.sectionId}`;
export const SECTION_TARGET = 'tgt:section';
export const rowKey = (path: NodePath): string => path.join('.');
export const rowTargetHandle = (path: NodePath): string =>
  `tgt:${rowKey(path)}`;
export const handleKey = (nodeId: string, handleId: string): string =>
  `${nodeId}|${handleId}`;

// ---------------------------------------------------------------------------
// Antworten einer Frage
// ---------------------------------------------------------------------------

const ANSWER_TYPES: AnswerType[] = [
  'BOOLEAN',
  'SINGLE_CHOICE',
  'TEXT',
  'NUMBER',
  'DATE',
];

export const asAnswerType = (value: string | undefined): AnswerType =>
  ANSWER_TYPES.includes(value as AnswerType) ? (value as AnswerType) : 'TEXT';

/**
 * Die Anschlüsse einer Frage — je möglicher Antwort einer, an dem eine Linie beginnt.
 *
 * Ja/Nein und die Optionen einer Auswahl sind eindeutig. Für Text, Zahl und Datum gibt es
 * keinen festen Wert; dort startet die Linie bei »Antwort« als »ist nicht leer« und wird im
 * Eigenschaftenbereich verfeinert (»ist größer als 100 000«).
 */
export const answersOf = (
  questionId: string,
  answerType: AnswerType,
  options: string[] = []
): Omit<ICanvasAnswer, 'y'>[] => {
  const left = { type: 'question' as const, questionId };
  if (answerType === 'BOOLEAN') {
    return [
      {
        handleId: `ans:${questionId}:true`,
        label: 'Ja',
        kind: 'yes',
        condition: { left, operator: 'equals', right: 'true' },
      },
      {
        handleId: `ans:${questionId}:false`,
        label: 'Nein',
        kind: 'no',
        condition: { left, operator: 'equals', right: 'false' },
      },
    ];
  }
  const filled = options.filter(option => option.trim() !== '');
  if (answerType === 'SINGLE_CHOICE' && filled.length > 0) {
    return filled.map((option, index) => ({
      handleId: `ans:${questionId}:opt:${index}`,
      label: option,
      kind: 'option' as const,
      condition: { left, operator: 'equals' as const, right: option },
    }));
  }
  return [
    {
      handleId: `ans:${questionId}:any`,
      label: 'Antwort',
      kind: 'any',
      condition: { left, operator: 'isNotEmpty', right: '' },
    },
  ];
};

/** Der Wortlaut eines Vergleichs ohne Subjekt — für die Beschriftung einer Linie. */
export const comparisonLabel = (condition: ICondition): string =>
  isUnary(condition.operator)
    ? OPERATOR_LABEL[condition.operator]
    : `${OPERATOR_LABEL[condition.operator]} „${answerLabel(condition.right)}"`;

/**
 * An welchem Anschluss eine bestehende Bedingung beginnt.
 *
 * `ist Ja` und `ist nicht Nein` sind dieselbe Aussage, beide hängen am Ja-Anschluss. Was sich
 * keinem Anschluss zuordnen lässt (»enthält …«), beginnt am ersten und trägt seinen Vergleich
 * als Beschriftung — sichtbar bleibt es in jedem Fall.
 */
export const matchAnswer = (
  condition: ICondition,
  answers: Omit<ICanvasAnswer, 'y'>[]
): { answer: Omit<ICanvasAnswer, 'y'>; exact: boolean } | null => {
  if (!answers.length) {
    return null;
  }
  const { operator, right } = condition;
  const exact = answers.find(answer => {
    const target = answer.condition;
    if (answer.kind === 'yes' || answer.kind === 'no') {
      const meint = answer.kind === 'yes' ? 'true' : 'false';
      const gegenteil = answer.kind === 'yes' ? 'false' : 'true';
      return (
        (operator === 'equals' && right === meint) ||
        (operator === 'notEquals' && right === gegenteil)
      );
    }
    if (answer.kind === 'option') {
      return operator === 'equals' && right === target.right;
    }
    return operator === 'isNotEmpty';
  });
  if (exact) {
    return { answer: exact, exact: true };
  }
  const any = answers.find(answer => answer.kind === 'any');
  return { answer: any ?? answers[0], exact: false };
};

// ---------------------------------------------------------------------------
// Zeilen einer Karte
// ---------------------------------------------------------------------------

const isConditionTarget = (node: IEditorNode): boolean =>
  node.type === 'conditional_text' ||
  node.type === 'conditional_block_reference';

/** Die Zeilen eines Abschnitts mit festen Höhen — auch die eines verschachtelten Zweigs. */
export const sectionRows = (section: IEditorSection): ICanvasRow[] => {
  const rows: ICanvasRow[] = [];
  let y = HEADER_HEIGHT;
  const visit = (nodes: IEditorNode[], parent: NodePath, depth: number) => {
    nodes.forEach((node, index) => {
      const path = [...parent, index];
      const answers =
        node.type === 'question' && node.questionId
          ? answersOf(
              node.questionId,
              asAnswerType(node.answerType),
              node.answerOptions ?? []
            )
          : [];
      const top = y;
      const height = ROW_HEIGHT + answers.length * ANSWER_HEIGHT;
      rows.push({
        key: rowKey(path),
        path,
        depth,
        node,
        y: top,
        height,
        answers: answers.map((answer, position) => ({
          ...answer,
          y: top + ROW_HEIGHT + position * ANSWER_HEIGHT + ANSWER_HEIGHT / 2,
        })),
        targetHandleId: isConditionTarget(node) ? rowTargetHandle(path) : null,
      });
      y += height;
      if (node.type === 'conditional_text' && node.nodes?.length) {
        visit(node.nodes, path, depth + 1);
      }
    });
  };
  visit(section.nodes, [], 0);
  return rows;
};

export const sectionHeight = (rows: ICanvasRow[]): number => {
  if (!rows.length) {
    return HEADER_HEIGHT + EMPTY_HEIGHT + CARD_PADDING;
  }
  const last = rows[rows.length - 1];
  return last.y + last.height + CARD_PADDING;
};

// ---------------------------------------------------------------------------
// Zugriff auf Bedingungen
// ---------------------------------------------------------------------------

export const nodeAtPath = (
  nodes: IEditorNode[],
  path: NodePath
): IEditorNode | null => {
  let current: IEditorNode | null = null;
  let list = nodes;
  for (const index of path) {
    current = list[index] ?? null;
    if (!current) {
      return null;
    }
    list = current.nodes ?? [];
  }
  return current;
};

export const conditionAt = (
  sections: IEditorSection[],
  target: ITargetRef
): ICondition | null => {
  const section = sections[target.sectionIndex];
  if (!section) {
    return null;
  }
  if (target.path === null) {
    return section.condition ?? null;
  }
  return nodeAtPath(section.nodes, target.path)?.condition ?? null;
};

const replaceNodeAtPath = (
  nodes: IEditorNode[],
  path: NodePath,
  update: (_node: IEditorNode) => IEditorNode
): IEditorNode[] => {
  const [index, ...rest] = path;
  const node = nodes[index];
  if (!node) {
    return nodes;
  }
  if (!rest.length) {
    return replaceAt(nodes, index, update(node));
  }
  return replaceAt(nodes, index, {
    ...node,
    nodes: replaceNodeAtPath(node.nodes ?? [], rest, update),
  });
};

/** Bedingung setzen oder (mit `null`) entfernen — am Abschnitt oder am Element. */
export const setConditionAt = (
  sections: IEditorSection[],
  target: ITargetRef,
  condition: ICondition | null
): IEditorSection[] => {
  const section = sections[target.sectionIndex];
  if (!section) {
    return sections;
  }
  if (target.path === null) {
    return replaceAt(sections, target.sectionIndex, { ...section, condition });
  }
  if (!nodeAtPath(section.nodes, target.path)) {
    return sections;
  }
  return replaceAt(sections, target.sectionIndex, {
    ...section,
    nodes: replaceNodeAtPath(section.nodes, target.path, node => ({
      ...node,
      condition,
    })),
  });
};

// ---------------------------------------------------------------------------
// Fragen
// ---------------------------------------------------------------------------

export const locateQuestions = (
  sections: IEditorSection[]
): Map<string, { sectionIndex: number; path: NodePath }> => {
  const result = new Map<string, { sectionIndex: number; path: NodePath }>();
  sections.forEach((section, sectionIndex) => {
    const visit = (nodes: IEditorNode[], parent: NodePath) =>
      nodes.forEach((node, index) => {
        const path = [...parent, index];
        if (node.type === 'question' && node.questionId) {
          if (!result.has(node.questionId)) {
            result.set(node.questionId, { sectionIndex, path });
          }
        }
        if (node.nodes?.length) {
          visit(node.nodes, path);
        }
      });
    visit(section.nodes, []);
  });
  return result;
};

const startsWith = (path: NodePath, prefix: NodePath): boolean =>
  prefix.length <= path.length &&
  prefix.every((value, index) => path[index] === value);

/**
 * Würde diese Bedingung ihre eigene Frage verstecken?
 *
 * Die Runtime fragt eine Frage nur, wenn ihre Stelle erreicht wird. Hängt ein Abschnitt an einer
 * Frage, die in ihm selbst steht (oder ein Zweig an einer Frage in diesem Zweig), wird die Stelle
 * nie erreicht — die Frage wird nie gestellt, die Bedingung nie entschieden, und der Teil fehlt
 * stillschweigend. Die Prüfung beim Veröffentlichen erkennt das nicht; deshalb hier.
 */
export const gatesItself = (
  questionLocation: { sectionIndex: number; path: NodePath } | undefined,
  target: ITargetRef
): boolean => {
  if (
    !questionLocation ||
    questionLocation.sectionIndex !== target.sectionIndex
  ) {
    return false;
  }
  if (target.path === null) {
    return true;
  }
  return (
    questionLocation.path.length > target.path.length &&
    startsWith(questionLocation.path, target.path)
  );
};

export interface ISelfGatingIssue {
  target: ITargetRef;
  questionId: string;
}

/** Alle Bedingungen, die ihre eigene Frage verstecken — als Warnung an der Karte. */
export const selfGatingIssues = (
  sections: IEditorSection[]
): ISelfGatingIssue[] => {
  const where = locateQuestions(sections);
  const issues: ISelfGatingIssue[] = [];
  const check = (target: ITargetRef, condition?: ICondition | null) => {
    if (condition?.left.type === 'question' && condition.left.questionId) {
      const location = where.get(condition.left.questionId);
      if (gatesItself(location, target)) {
        issues.push({ target, questionId: condition.left.questionId });
      }
    }
  };
  sections.forEach((section, sectionIndex) => {
    check({ sectionIndex, path: null }, section.condition);
    const visit = (nodes: IEditorNode[], parent: NodePath) =>
      nodes.forEach((node, index) => {
        const path = [...parent, index];
        check({ sectionIndex, path }, node.condition);
        if (node.nodes?.length) {
          visit(node.nodes, path);
        }
      });
    visit(section.nodes, []);
  });
  return issues;
};

export interface IQuestionOverview {
  questionId: string;
  prompt: string;
  answerType: AnswerType;
  sectionIndex: number | null;
  sectionTitle: string;
  /** Wie viele Bedingungen sich auf sie beziehen. */
  usedBy: number;
  external: boolean;
}

/** Alle Fragen in der Reihenfolge, in der sie im Dokument stehen — mit ihrer Wirkung. */
export const questionOverview = (
  sections: IEditorSection[],
  externals: IExternalQuestion[] = []
): IQuestionOverview[] => {
  const usage = new Map<string, number>();
  const count = (condition?: ICondition | null) => {
    if (condition?.left.type === 'question' && condition.left.questionId) {
      usage.set(
        condition.left.questionId,
        (usage.get(condition.left.questionId) ?? 0) + 1
      );
    }
  };
  sections.forEach(section => {
    count(section.condition);
    walkNodes(section.nodes).forEach(node => count(node.condition));
  });
  const result: IQuestionOverview[] = [];
  const seen = new Set<string>();
  sections.forEach((section, sectionIndex) =>
    walkNodes(section.nodes).forEach(node => {
      if (
        node.type === 'question' &&
        node.questionId &&
        !seen.has(node.questionId)
      ) {
        seen.add(node.questionId);
        result.push({
          questionId: node.questionId,
          prompt: (node.prompt || '').trim() || node.questionId,
          answerType: asAnswerType(node.answerType),
          sectionIndex,
          sectionTitle: section.title,
          usedBy: usage.get(node.questionId) ?? 0,
          external: false,
        });
      }
    })
  );
  externals.forEach(item => {
    if (item.questionId && !seen.has(item.questionId)) {
      seen.add(item.questionId);
      result.push({
        questionId: item.questionId,
        prompt:
          item.prompt.trim() ||
          (item.blockName ? `Frage aus „${item.blockName}"` : item.questionId),
        answerType: asAnswerType(item.answerType),
        sectionIndex: null,
        sectionTitle: item.blockName ?? 'Unterbaustein',
        usedBy: usage.get(item.questionId) ?? 0,
        external: true,
      });
    }
  });
  return result;
};

// ---------------------------------------------------------------------------
// Prüfungen auf Vorgangsdaten
// ---------------------------------------------------------------------------

/** Gleiche Prüfung, gleiche Kennung — eine Prüfung kann mehrere Ziele haben. */
export const checkSignature = (condition: ICondition): string =>
  [
    condition.left.type,
    condition.left.selector ?? condition.left.value ?? '',
    condition.operator,
    condition.right ?? '',
  ].join('|');

export const checkNodeId = (condition: ICondition): string =>
  `f:${checkSignature(condition)}`;

export const CHECK_SOURCE = 'out';

// ---------------------------------------------------------------------------
// Der Graph
// ---------------------------------------------------------------------------

interface IEdgeDraft extends Omit<IConditionEdge, 'lane'> {
  top: number;
  bottom: number;
}

/**
 * Spuren rechts der Karten, damit sich Linien nicht übereinanderlegen.
 *
 * Jede Linie belegt das Stück zwischen Quelle und Ziel; überlappen sich zwei Stücke, bekommen
 * sie verschiedene Spuren. Die kürzesten Linien liegen innen — so kreuzen sich am wenigsten.
 */
export const assignLanes = (
  spans: { id: string; top: number; bottom: number }[]
): Map<string, number> => {
  const lanes = new Map<string, number>();
  const occupied: { top: number; bottom: number }[][] = [];
  [...spans]
    .sort((a, b) => a.bottom - a.top - (b.bottom - b.top) || a.top - b.top)
    .forEach(span => {
      let lane = 0;
      while (
        (occupied[lane] ?? []).some(
          other => span.top <= other.bottom && other.top <= span.bottom
        )
      ) {
        lane += 1;
      }
      (occupied[lane] = occupied[lane] ?? []).push(span);
      lanes.set(span.id, lane);
    });
  return lanes;
};

/**
 * Der ganze Graph aus den Abschnitten.
 *
 * `pendingChecks` sind Prüfungen, die gerade auf die Fläche gezogen, aber noch mit keinem Ziel
 * verbunden wurden — im Modell gibt es sie noch nicht, also leben sie nur in der Ansicht. Sie
 * stehen dort, wo sie abgelegt wurden (`nearY`), sonst am Ende.
 */
export const buildGraph = (
  sections: IEditorSection[],
  externalQuestions: IExternalQuestion[] = [],
  pendingChecks: { id: string; condition: ICondition; nearY?: number }[] = []
): IBaukastenGraph => {
  const sources = new Map<string, IAnswerSource>();
  const targets = new Map<string, ITargetRef>();
  const questionLocation = locateQuestions(sections);

  // 1. Abschnitte untereinander, in der Reihenfolge der Urkunde.
  const start = { x: (SECTION_WIDTH - TERMINAL_WIDTH) / 2, y: 0 };
  let y = TERMINAL_HEIGHT + SECTION_GAP;
  const layouts: ISectionLayout[] = sections.map((section, index) => {
    const rows = sectionRows(section);
    const layout: ISectionLayout = {
      id: sectionNodeId(section),
      index,
      section,
      x: 0,
      y,
      width: SECTION_WIDTH,
      height: sectionHeight(rows),
      rows,
      targetHandleId: SECTION_TARGET,
    };
    y += layout.height + SECTION_GAP;
    targets.set(handleKey(layout.id, SECTION_TARGET), {
      sectionIndex: index,
      path: null,
    });
    rows.forEach(row => {
      if (row.targetHandleId) {
        targets.set(handleKey(layout.id, row.targetHandleId), {
          sectionIndex: index,
          path: row.path,
        });
      }
      row.answers.forEach(answer =>
        sources.set(handleKey(layout.id, answer.handleId), {
          nodeId: layout.id,
          handleId: answer.handleId,
          questionId: row.node.questionId ?? null,
          condition: answer.condition,
        })
      );
    });
    return layout;
  });
  const end = { x: start.x, y };

  // Wo jede Frage auf der Fläche steht — absolut, für Richtung und Spuren.
  const answerYByHandle = new Map<string, number>();
  layouts.forEach(layout =>
    layout.rows.forEach(row =>
      row.answers.forEach(answer =>
        answerYByHandle.set(
          handleKey(layout.id, answer.handleId),
          layout.y + answer.y
        )
      )
    )
  );
  const targetY = (target: ITargetRef): number => {
    const layout = layouts[target.sectionIndex];
    if (target.path === null) {
      return layout.y + HEADER_HEIGHT / 2;
    }
    const row = layout.rows.find(item => item.key === rowKey(target.path!));
    return layout.y + (row ? row.y + ROW_HEIGHT / 2 : HEADER_HEIGHT / 2);
  };
  const targetHandleOf = (target: ITargetRef): string =>
    target.path === null ? SECTION_TARGET : rowTargetHandle(target.path);

  // 2. Bedingungen einsammeln.
  const conditions: { target: ITargetRef; condition: ICondition }[] = [];
  layouts.forEach(layout => {
    if (layout.section.condition) {
      conditions.push({
        target: { sectionIndex: layout.index, path: null },
        condition: layout.section.condition,
      });
    }
    layout.rows.forEach(row => {
      if (row.targetHandleId && row.node.condition) {
        conditions.push({
          target: { sectionIndex: layout.index, path: row.path },
          condition: row.node.condition,
        });
      }
    });
  });

  // Fremde Fragen (aus Unterbausteinen), die eine Bedingung braucht oder die es gibt.
  const externalById = new Map<string, IExternalQuestion>();
  externalQuestions.forEach(item => {
    if (item.questionId && !questionLocation.has(item.questionId)) {
      externalById.set(item.questionId, item);
    }
  });

  const drafts: IEdgeDraft[] = [];
  const checkTargets = new Map<
    string,
    { condition: ICondition; ys: number[] }
  >();
  const usedExternals = new Set<string>();

  conditions.forEach(({ target, condition }, position) => {
    const targetLayout = layouts[target.sectionIndex];
    const tY = targetY(target);
    const base = {
      target: targetLayout.id,
      targetHandle: targetHandleOf(target),
      targetRef: target,
    };
    if (condition.left.type === 'question') {
      const questionId = condition.left.questionId || '';
      const location = questionLocation.get(questionId);
      if (location) {
        const layout = layouts[location.sectionIndex];
        const row = layout.rows.find(
          item => item.key === rowKey(location.path)
        );
        const match = row ? matchAnswer(condition, row.answers) : null;
        if (!row || !match) {
          return;
        }
        const sY =
          answerYByHandle.get(handleKey(layout.id, match.answer.handleId)) ?? 0;
        drafts.push({
          ...base,
          id: `e:${position}`,
          source: layout.id,
          sourceHandle: match.answer.handleId,
          kind: match.exact ? match.answer.kind : 'any',
          label: match.exact ? match.answer.label : comparisonLabel(condition),
          backward:
            location.sectionIndex > target.sectionIndex ||
            (location.sectionIndex === target.sectionIndex && sY > tY),
          questionId,
          top: Math.min(sY, tY),
          bottom: Math.max(sY, tY),
        });
        return;
      }
      if (externalById.has(questionId)) {
        usedExternals.add(questionId);
        const external = externalById.get(questionId)!;
        const answers = answersOf(
          questionId,
          asAnswerType(external.answerType),
          external.options ?? []
        );
        const match = matchAnswer(condition, answers);
        if (!match) {
          return;
        }
        drafts.push({
          ...base,
          id: `e:${position}`,
          source: `x:${questionId}`,
          sourceHandle: match.answer.handleId,
          kind: match.exact ? match.answer.kind : 'any',
          label: match.exact ? match.answer.label : comparisonLabel(condition),
          backward: false,
          questionId,
          top: tY,
          bottom: tY,
        });
      }
      // Eine Frage, die es nicht gibt: keine Linie — die Karte zeigt eine offene Bedingung.
      return;
    }
    const id = checkNodeId(condition);
    const entry = checkTargets.get(id) ?? { condition, ys: [] };
    entry.ys.push(tY);
    checkTargets.set(id, entry);
    drafts.push({
      ...base,
      id: `e:${position}`,
      source: id,
      sourceHandle: CHECK_SOURCE,
      kind: 'field',
      label: '',
      backward: false,
      questionId: null,
      top: tY,
      bottom: tY,
    });
  });

  // 3. Spuren für die Linien zwischen Fragen und Zielen in der Kartenspalte.
  const columnDrafts = drafts.filter(
    draft => draft.kind !== 'field' && !draft.source.startsWith('x:')
  );
  const lanes = assignLanes(
    columnDrafts.map(draft => ({
      id: draft.id,
      top: draft.top,
      bottom: draft.bottom,
    }))
  );
  const laneCount = lanes.size
    ? Math.max(...Array.from(lanes.values())) + 1
    : 0;
  const sideColumnX = SECTION_WIDTH + LANE_BASE + laneCount * LANE_STEP + 140;

  // 4. Rechte Spalte: Prüfungen und fremde Fragen, jeweils neben ihrem ersten Ziel.
  const placed: { y: number; height: number }[] = [];
  const place = (wanted: number, height: number): number => {
    let candidate = Math.max(0, wanted - height / 2);
    const sorted = [...placed].sort((a, b) => a.y - b.y);
    for (const block of sorted) {
      if (
        candidate < block.y + block.height + CHECK_GAP &&
        candidate + height > block.y - CHECK_GAP
      ) {
        candidate = block.y + block.height + CHECK_GAP;
      }
    }
    placed.push({ y: candidate, height });
    return candidate;
  };

  const externals: IExternalQuestionLayout[] = [];
  Array.from(externalById.values()).forEach(item => {
    const answers = answersOf(
      item.questionId,
      asAnswerType(item.answerType),
      item.options ?? []
    );
    const id = `x:${item.questionId}`;
    const firstTarget = drafts.find(draft => draft.source === id);
    if (!firstTarget && !usedExternals.has(item.questionId)) {
      // Unbenutzte fremde Fragen stehen in der Fragenübersicht, nicht auf der Fläche.
      return;
    }
    const height = ROW_HEIGHT + 8 + answers.length * ANSWER_HEIGHT;
    const top = place(firstTarget ? firstTarget.top : 0, height);
    externals.push({
      id,
      questionId: item.questionId,
      prompt:
        item.prompt.trim() ||
        (item.blockName ? `Frage aus „${item.blockName}"` : item.questionId),
      answerType: asAnswerType(item.answerType),
      options: item.options ?? [],
      x: sideColumnX,
      y: top,
      height,
      answers: answers.map((answer, position) => ({
        ...answer,
        y: ROW_HEIGHT + 8 + position * ANSWER_HEIGHT + ANSWER_HEIGHT / 2,
      })),
    });
    answers.forEach(answer =>
      sources.set(handleKey(id, answer.handleId), {
        nodeId: id,
        handleId: answer.handleId,
        questionId: item.questionId,
        condition: answer.condition,
      })
    );
  });

  const checks: IFieldCheckLayout[] = [];
  Array.from(checkTargets.entries())
    .sort((a, b) => Math.min(...a[1].ys) - Math.min(...b[1].ys))
    .forEach(([id, entry]) => {
      checks.push({
        id,
        condition: entry.condition,
        x: sideColumnX,
        y: place(Math.min(...entry.ys), CHECK_HEIGHT),
        pending: false,
      });
      sources.set(handleKey(id, CHECK_SOURCE), {
        nodeId: id,
        handleId: CHECK_SOURCE,
        questionId: null,
        condition: entry.condition,
      });
    });
  pendingChecks.forEach(pending => {
    if (checkTargets.has(checkNodeId(pending.condition))) {
      return;
    }
    checks.push({
      id: pending.id,
      condition: pending.condition,
      x: sideColumnX,
      y: place(pending.nearY ?? end.y, CHECK_HEIGHT),
      pending: true,
    });
    sources.set(handleKey(pending.id, CHECK_SOURCE), {
      nodeId: pending.id,
      handleId: CHECK_SOURCE,
      questionId: null,
      condition: pending.condition,
    });
  });

  const edges: IConditionEdge[] = drafts.map(
    ({ top: _top, bottom: _bottom, ...draft }) => ({
      ...draft,
      lane: lanes.get(draft.id) ?? 0,
    })
  );

  return {
    sections: layouts,
    checks,
    externals,
    edges,
    start,
    end,
    sources,
    targets,
    questionLocation,
    sideColumnX,
  };
};

// ---------------------------------------------------------------------------
// Verbinden und Lösen
// ---------------------------------------------------------------------------

export type ConnectResult =
  | { ok: true; sections: IEditorSection[]; replaced: boolean }
  | { ok: false; message: string };

/**
 * Eine Linie ziehen heißt: die Bedingung des Ziels setzen.
 *
 * Ein Ziel hat genau eine Bedingung (das Modell kennt kein »und« an einer Stelle); eine neue
 * Linie ersetzt also die alte. Ein »und« entsteht wie im Listen-Editor durch Verschachteln.
 */
export const connect = (
  sections: IEditorSection[],
  graph: IBaukastenGraph,
  source: { nodeId: string; handleId: string },
  target: { nodeId: string; handleId: string }
): ConnectResult => {
  const from = graph.sources.get(handleKey(source.nodeId, source.handleId));
  const to = graph.targets.get(handleKey(target.nodeId, target.handleId));
  if (!from || !to) {
    return {
      ok: false,
      message:
        'Eine Linie verbindet eine Antwort (oder eine Prüfung) mit einem Abschnitt oder einem bedingten Element.',
    };
  }
  if (
    from.questionId &&
    gatesItself(graph.questionLocation.get(from.questionId), to)
  ) {
    return {
      ok: false,
      message:
        'Diese Frage steht selbst in dem Teil, den sie steuern soll — sie würde nie gestellt. Setzen Sie die Frage in einen früheren Abschnitt.',
    };
  }
  const previous = conditionAt(sections, to);
  return {
    ok: true,
    sections: setConditionAt(sections, to, { ...from.condition }),
    replaced: Boolean(previous),
  };
};

/** Gilt diese Verbindung? Für die Rückmeldung schon beim Ziehen. */
export const canConnect = (
  graph: IBaukastenGraph,
  source: { nodeId: string; handleId: string },
  target: { nodeId: string; handleId: string }
): boolean => {
  const from = graph.sources.get(handleKey(source.nodeId, source.handleId));
  const to = graph.targets.get(handleKey(target.nodeId, target.handleId));
  if (!from || !to) {
    return false;
  }
  return !(
    from.questionId &&
    gatesItself(graph.questionLocation.get(from.questionId), to)
  );
};

/** Alle Ziele einer Prüfung gleichzeitig ändern — sie ist eine Prüfung, nicht viele. */
export const replaceCheckEverywhere = (
  sections: IEditorSection[],
  before: ICondition,
  after: ICondition | null
): IEditorSection[] => {
  const signature = checkSignature(before);
  const same = (condition?: ICondition | null) =>
    Boolean(condition) &&
    condition!.left.type !== 'question' &&
    checkSignature(condition!) === signature;
  const rewrite = (nodes: IEditorNode[]): IEditorNode[] =>
    nodes.map(node => ({
      ...node,
      ...(same(node.condition) ? { condition: after } : {}),
      ...(node.nodes ? { nodes: rewrite(node.nodes) } : {}),
    }));
  return sections.map(section => ({
    ...section,
    ...(same(section.condition) ? { condition: after } : {}),
    nodes: rewrite(section.nodes),
  }));
};

// ---------------------------------------------------------------------------
// Einfügen und Verschieben
// ---------------------------------------------------------------------------

/** Eine Stelle zum Einfügen: im Abschnitt (`parentPath = []`) oder im Zweig eines Elements. */
export interface IInsertPoint {
  sectionIndex: number;
  parentPath: NodePath;
  index: number;
}

const withChildrenAt = (
  nodes: IEditorNode[],
  parentPath: NodePath,
  update: (_children: IEditorNode[]) => IEditorNode[]
): IEditorNode[] =>
  parentPath.length
    ? replaceNodeAtPath(nodes, parentPath, node => ({
        ...node,
        nodes: update(node.nodes ?? []),
      }))
    : update(nodes);

/** Darf diese Art in einen Zweig? Fragen gehören in einen Abschnitt (siehe `BRANCH_NODE_KINDS`). */
export const allowedInBranch = (kind: NodeKind): boolean =>
  BRANCH_NODE_KINDS.includes(kind);

export const insertNode = (
  sections: IEditorSection[],
  point: IInsertPoint,
  node: IEditorNode
): IEditorSection[] => {
  const section = sections[point.sectionIndex];
  if (!section) {
    return sections;
  }
  if (point.parentPath.length && !allowedInBranch(node.type)) {
    return sections;
  }
  return replaceAt(sections, point.sectionIndex, {
    ...section,
    nodes: withChildrenAt(section.nodes, point.parentPath, children =>
      insertAt(
        children,
        Math.max(0, Math.min(point.index, children.length)),
        node
      )
    ),
  });
};

export const removeNode = (
  sections: IEditorSection[],
  sectionIndex: number,
  path: NodePath
): IEditorSection[] => {
  const section = sections[sectionIndex];
  if (!section || !path.length) {
    return sections;
  }
  const parentPath = path.slice(0, -1);
  const index = path[path.length - 1];
  return replaceAt(sections, sectionIndex, {
    ...section,
    nodes: withChildrenAt(section.nodes, parentPath, children =>
      removeAt(children, index)
    ),
  });
};

export const replaceNode = (
  sections: IEditorSection[],
  sectionIndex: number,
  path: NodePath,
  node: IEditorNode
): IEditorSection[] => {
  const section = sections[sectionIndex];
  if (!section || !path.length) {
    return sections;
  }
  return replaceAt(sections, sectionIndex, {
    ...section,
    nodes: replaceNodeAtPath(section.nodes, path, () => node),
  });
};

/**
 * Ein Element an eine andere Stelle setzen — im selben Abschnitt oder in einen anderen.
 *
 * Nicht in sich selbst hinein (ein Zweig in seinen eigenen Zweig) und keine Frage in einen
 * Zweig. Liegt die alte Stelle vor der neuen im selben Zweig, rückt der Index nach dem
 * Herausnehmen um eins nach vorn.
 */
export const moveNode = (
  sections: IEditorSection[],
  from: { sectionIndex: number; path: NodePath },
  to: IInsertPoint
): IEditorSection[] => {
  const node = nodeAtPath(sections[from.sectionIndex]?.nodes ?? [], from.path);
  if (!node) {
    return sections;
  }
  if (to.parentPath.length && !allowedInBranch(node.type)) {
    return sections;
  }
  if (
    from.sectionIndex === to.sectionIndex &&
    startsWith(to.parentPath, from.path)
  ) {
    return sections;
  }
  const fromParent = from.path.slice(0, -1);
  const fromIndex = from.path[from.path.length - 1];
  let target = { ...to, parentPath: [...to.parentPath] };
  if (from.sectionIndex === to.sectionIndex) {
    // Ein Pfad hinter der alten Stelle im selben Zweig rückt nach dem Herausnehmen auf.
    const depth = fromParent.length;
    if (
      target.parentPath.length > depth &&
      startsWith(target.parentPath, fromParent) &&
      target.parentPath[depth] > fromIndex
    ) {
      target.parentPath[depth] -= 1;
    }
    if (
      rowKey(target.parentPath) === rowKey(fromParent) &&
      target.index > fromIndex
    ) {
      target = { ...target, index: target.index - 1 };
    }
    if (
      rowKey(target.parentPath) === rowKey(fromParent) &&
      target.index === fromIndex
    ) {
      return sections;
    }
  }
  const without = removeNode(sections, from.sectionIndex, from.path);
  return insertNode(without, target, node);
};

/** Einen Abschnitt an eine Stelle einfügen. */
export const insertSectionAt = (
  sections: IEditorSection[],
  index: number,
  nodes: IEditorNode[] = []
): { sections: IEditorSection[]; index: number } => {
  const section = { ...newSection(sections), nodes };
  const at = Math.max(0, Math.min(index, sections.length));
  return { sections: insertAt(sections, at, section), index: at };
};

export const moveSectionTo = (
  sections: IEditorSection[],
  from: number,
  to: number
): IEditorSection[] => {
  let next = sections;
  const target = Math.max(0, Math.min(to, sections.length - 1));
  let current = from;
  while (current !== target) {
    const step = target > current ? 1 : -1;
    next = moveItem(next, current, step);
    current += step;
  }
  return next;
};

/** Zwischen welche Abschnitte gehört eine Stelle auf der Fläche? Für Ablegen und Umordnen. */
export const sectionInsertIndexAt = (
  layouts: ISectionLayout[],
  flowY: number,
  ignore?: number
): number => {
  let index = 0;
  layouts.forEach(layout => {
    if (layout.index === ignore) {
      return;
    }
    if (flowY > layout.y + layout.height / 2) {
      index = layout.index + 1;
    }
  });
  if (ignore !== undefined && index > ignore) {
    index -= 1;
  }
  return index;
};

export const sectionAt = (
  layouts: ISectionLayout[],
  flowX: number,
  flowY: number
): ISectionLayout | null =>
  layouts.find(
    layout =>
      flowX >= layout.x &&
      flowX <= layout.x + layout.width &&
      flowY >= layout.y &&
      flowY <= layout.y + layout.height
  ) ?? null;

/**
 * Wo ein Element landet, das über einer Karte losgelassen wird.
 *
 * Oberes Drittel einer Zeile: davor. Unteres Drittel: danach. Mitte einer Bedingung: in ihren
 * Zweig — wenn die Art dort stehen darf. Über dem Kopf: an den Anfang; unter der letzten Zeile:
 * ans Ende.
 */
export const insertPointAt = (
  layout: ISectionLayout,
  yInCard: number,
  kind: NodeKind | null
): IInsertPoint & { into: string | null } => {
  const base = { sectionIndex: layout.index };
  if (yInCard < HEADER_HEIGHT || !layout.rows.length) {
    return { ...base, parentPath: [], index: 0, into: null };
  }
  const row = layout.rows.find(
    item => yInCard >= item.y && yInCard < item.y + item.height
  );
  if (!row) {
    return {
      ...base,
      parentPath: [],
      index: layout.section.nodes.length,
      into: null,
    };
  }
  const branchOk = kind === null || allowedInBranch(kind);
  const third = row.height / 3;
  const parentPath = row.path.slice(0, -1);
  const index = row.path[row.path.length - 1];
  if (
    row.node.type === 'conditional_text' &&
    branchOk &&
    yInCard >= row.y + third &&
    yInCard < row.y + 2 * third
  ) {
    return {
      ...base,
      parentPath: row.path,
      index: row.node.nodes?.length ?? 0,
      into: row.key,
    };
  }
  const after = yInCard >= row.y + row.height / 2;
  if (parentPath.length && !branchOk) {
    // Eine Frage über einem Zweig: vor bzw. hinter das äußerste Element dieses Zweigs.
    return {
      ...base,
      parentPath: [],
      index: row.path[0] + (after ? 1 : 0),
      into: null,
    };
  }
  return { ...base, parentPath, index: index + (after ? 1 : 0), into: null };
};

/** Die y-Position einer Einfügemarke in der Karte — für die Anzeige beim Ziehen. */
export const insertMarkerY = (
  layout: ISectionLayout,
  point: IInsertPoint
): number => {
  const siblings = layout.rows.filter(
    row =>
      row.path.length === point.parentPath.length + 1 &&
      startsWith(row.path, point.parentPath)
  );
  if (!siblings.length) {
    const parent = layout.rows.find(
      row => row.key === rowKey(point.parentPath)
    );
    return parent ? parent.y + parent.height : HEADER_HEIGHT;
  }
  const at = siblings.find(
    row => row.path[row.path.length - 1] === point.index
  );
  if (at) {
    return at.y;
  }
  // Hinter das letzte Geschwister — hinter dessen ganzen Zweig.
  const last = siblings[siblings.length - 1];
  const inside = layout.rows.filter(row => startsWith(row.path, last.path));
  const bottom = inside[inside.length - 1];
  return bottom.y + bottom.height;
};

const rewriteConditions = (
  sections: IEditorSection[],
  rewrite: (_condition: ICondition) => ICondition | null | undefined
): IEditorSection[] => {
  const apply = (condition?: ICondition | null) => {
    if (!condition) {
      return condition;
    }
    const next = rewrite(condition);
    return next === undefined ? condition : next;
  };
  const visit = (nodes: IEditorNode[]): IEditorNode[] =>
    nodes.map(node => ({
      ...node,
      ...(node.condition !== undefined
        ? { condition: apply(node.condition) }
        : {}),
      ...(node.nodes ? { nodes: visit(node.nodes) } : {}),
    }));
  return sections.map(section => ({
    ...section,
    condition: apply(section.condition),
    nodes: visit(section.nodes),
  }));
};

const refersTo = (condition: ICondition, questionId: string): boolean =>
  condition.left.type === 'question' &&
  condition.left.questionId === questionId;

/**
 * Bedingungen auf eine Frage entfernen — wenn die Frage selbst gelöscht wird.
 *
 * Bliebe die Bedingung stehen, zeigte sie auf eine Frage, die es nicht gibt; die Prüfung lehnt
 * das ab, und die Bearbeiterin sucht einen Fehler, den sie nicht gemacht hat.
 */
export const dropConditionsOn = (
  sections: IEditorSection[],
  questionId: string
): IEditorSection[] =>
  rewriteConditions(sections, condition =>
    refersTo(condition, questionId) ? null : undefined
  );

/** Wie viele Bedingungen hängen an diesen Fragen? */
export const conditionsOnQuestions = (
  sections: IEditorSection[],
  questionIds: string[]
): number => {
  let count = 0;
  rewriteConditions(sections, condition => {
    if (questionIds.some(id => refersTo(condition, id))) {
      count += 1;
    }
    return undefined;
  });
  return count;
};

/**
 * Eine Frage umbenennen und ihre Linien mitnehmen.
 *
 * Nur wenn der neue Schlüssel frei ist: sonst würden zwei Fragen zu einer, und die Linien der
 * anderen hingen plötzlich an dieser.
 */
export const renameQuestionReferences = (
  sections: IEditorSection[],
  from: string,
  to: string
): IEditorSection[] => {
  if (!from || !to || from === to || locateQuestions(sections).has(to)) {
    return sections;
  }
  return rewriteConditions(sections, condition =>
    refersTo(condition, from)
      ? { ...condition, left: { ...condition.left, questionId: to } }
      : undefined
  );
};

/** Wo steht dieses Element (nach einem Verschieben)? Gesucht wird das Objekt selbst. */
export const findNodePath = (
  sections: IEditorSection[],
  wanted: IEditorNode
): { sectionIndex: number; path: NodePath } | null => {
  for (
    let sectionIndex = 0;
    sectionIndex < sections.length;
    sectionIndex += 1
  ) {
    const search = (
      nodes: IEditorNode[],
      parent: NodePath
    ): NodePath | null => {
      for (let index = 0; index < nodes.length; index += 1) {
        const path = [...parent, index];
        if (nodes[index] === wanted) {
          return path;
        }
        const inner = search(nodes[index].nodes ?? [], path);
        if (inner) {
          return inner;
        }
      }
      return null;
    };
    const path = search(sections[sectionIndex].nodes, []);
    if (path) {
      return { sectionIndex, path };
    }
  }
  return null;
};

/** Alle Fragekennungen des Baums — für neue, eindeutige Kennungen. */
export const usedQuestionIds = (
  sections: IEditorSection[],
  externals: IExternalQuestion[] = []
): string[] => [
  ...Array.from(locateQuestions(sections).keys()),
  ...externals.map(item => item.questionId).filter(Boolean),
];
